import { message } from "antd";
import { useCallback, useState } from "react";
import { receiptAPIWithErrorHandling } from "../../../api/receipt/receipt";
import ModalHistoryReceiptExport from "./ModalHistoryReceiptExport";

/**
 * Logic dùng chung cho việc xuất phiếu thu, chia sẻ giữa trang chi tiết đợt thu
 * (/collections/detail/:id) và trang chi tiết đơn vị (/collections/information/:id).
 *
 * Vì sao là hook chứ không phải component nút: hai trang đặt nút ở những chỗ khác nhau, và
 * trang chi tiết đợt thu còn gọi xuất từ TỪNG DÒNG trong bảng đơn vị - chỗ đó không nhét
 * một cụm nút dùng chung vào được. Nên phần dùng chung là logic, còn chỗ đặt nút để mỗi
 * trang tự quyết.
 *
 * Xuất hàng loạt không tải file ngay: một đơn vị 400 phiếu mất khoảng 30 giây, xuất toàn bộ
 * tới vài phút, trong khi axios cắt ở 20 giây. Backend đẩy sang hàng đợi, người dùng theo
 * dõi và tải file trong lịch sử.
 *
 * @param {object}        opts
 * @param {number|string} opts.rootId  đợt thu CHA - lịch sử lọc theo trường này nên trang
 *                                     đơn vị phải truyền parent_id, không phải id của nó.
 *
 * @returns {{
 *   queueExport: (collectionId: number|string, scope?: 'child'|'parent') => Promise<void>,
 *   exporting: boolean,
 *   exportingId: number|string|null,
 *   openHistory: () => void,
 *   historyModal: JSX.Element,
 * }}
 */
export default function useReceiptExport({ rootId }) {
    const [open, setOpen] = useState(false);
    const [exportingId, setExportingId] = useState(null);

    const openHistory = useCallback(() => setOpen(true), []);

    /**
     * Ghi nhận yêu cầu xuất rồi mở lịch sử.
     *
     * @param collectionId đợt thu con (scope 'child') hoặc đợt thu cha (scope 'parent')
     */
    const queueExport = useCallback(async (collectionId, scope = "child") => {
        const messageKey = "receipt-export";

        try {
            setExportingId(collectionId);
            message.loading({ key: messageKey, content: "Đang ghi nhận yêu cầu xuất phiếu thu..." });

            const res = scope === "parent"
                ? await receiptAPIWithErrorHandling.exportAllParentReceiptPdf(collectionId)
                : await receiptAPIWithErrorHandling.exportAllReceiptPdf(collectionId);

            if (!res?.success) {
                throw new Error(res?.message || "Không thể xuất phiếu thu");
            }

            message.success({ key: messageKey, content: res.message, duration: 5 });

            // Mở luôn lịch sử: đây là chỗ duy nhất thấy được tiến độ và tải file về.
            setOpen(true);
        } catch (error) {
            message.error({
                key: messageKey,
                content: error?.message || "Không thể xuất phiếu thu",
                duration: 4,
            });
        } finally {
            setExportingId(null);
        }
    }, []);

    const historyModal = (
        <ModalHistoryReceiptExport open={open} onClose={() => setOpen(false)} rootId={rootId} />
    );

    return {
        queueExport,
        exporting: exportingId !== null,
        exportingId,
        openHistory,
        historyModal,
    };
}
